
// Vue / Vuex
import { Component, Vue, Model, Emit, Prop } from "vue-property-decorator";

// Bulk import components
import BulkImportTable from './BulkImportTable.vue';

// Form Components
import DragDropUpload from "@/components/form/DragDropUpload.vue";
import VSelect3 from "vselect3";

// Bulk Import Types
import BulkImport, { csvHeader, BulkType, ImportBulkType } from '@/bulk-import/BulkImport';
import api from '@/services/api.service';

@Component({
    components: {
        "bulk-import-table": BulkImportTable,
        "drag-drop-upload": DragDropUpload,
        "v-select3": VSelect3,
    }
})
export default class BulkImportModal extends Vue {
    /**
     * @summary Model which handles 2 way data binding for visible
     * @summary state determines the modal display
     */
    @Model('input', { default: false, type: Boolean }) visible!: boolean;

    /**
     * @summary Prop which handles an array of { bulkType && metaData }
     */
    @Prop({ default: () => [], type: Array }) bulkImports: ImportBulkType<any, any>[];

    private selectedBulkImport: { bulkType: BulkType<any, any>, metaData: BulkImport[] } | null = null;

    private csvData: any[] = [];
    private completedData: any[] = [];
    private invalid: boolean = false;
    private completedCsvUpload: boolean = false;
    private submissionAttempts: number = 0;
    private submittedIds: number[] = [];

    // File upload
    private uploadTile: boolean = false;
    private acceptableFileExtensions: string[] = ['csv'];
    private dragDropKey: number = 1;

    private serverSideValidation = {};

    /**
     * @summary Emit method to change the visible state
     */
    @Emit('visible')
    private setVisibleState(visible: boolean): void {
        //
    }

    /**
     * @summary Method which attempts to save the bulk import data.
     */
    private async completeBulkUpload(): Promise<void> {
        this.submittedIds = this.completedData.map(x => x.id);
        const result = await api.saveBulkImport(this.selectedBulkImport.bulkType.bulkImportType, this.completedData);

        if (result.status == 200 && result.data) {
            if (!result.data.invalid) {
                this.completedCsvUpload = true;
                this.csvData = [];
            } else {
                this.serverSideValidation = result.data.invalidDataMessages;
            }
            this.submissionAttempts++;
        }
    }

    /**
     * @summary Metohod callback which handles failure of file upload attempt
     */
    private onInvalidFileDropped(file: File, msg: string | undefined = undefined): void {
        this.$notify({
            type: "error",
            title: "Bulk Import",
            text: msg ?? `Unable to upload the file provided (${file.name}) as it was not a valid CSV file`
        });
    }

    /**
     * @summary Method which handles attempt to call api and parse csv file.
     */
    private async fileUpload(file: File, progressHandler: (number) => void): Promise<any[]> {
        const response = await api.bulkImportParse<any[]>(file,  progressHandler, this.selectedBulkImport.bulkType.bulkImportType);
        if (!response?.data) {
            this.onInvalidFileDropped(file, `Failed to convert ${file.name} or failed to communicate to data service.`);
            return [];
        }

        return response.data;
    }

    /**
     * @summary Method callback which handles data when successful parse
     * @summary of bulk import csv.
     */
    private onFileUploaded(file: File, data: any[]): void {
        this.completedCsvUpload = false;

        this.csvData = data;
        this.serverSideValidation = {};
        this.uploadTile = false;
    }

    /**
     * @summary Method to execute custom bulkType import Template download
     */
    private downloadTemplate(): void {
        this.selectedBulkImport?.bulkType.bulkImportCsvTemplate();
    }

    /**
     * @summary Computed property to display the current bulk import title
     */
    private get bulkImportTitle(): string {
        return this.selectedBulkImport?.bulkType?.title ?? 'Choose a bulk import type';
    }

    private get dialogSize(): string {
        if (this.completedCsvUpload) return "lg";
        return this.csvData.length ? "xlg" : "lg";
    }
}
